import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import Layout from '../../layout/Layout';
import { permissions } from '../../common/permissionGroup';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import PermissionsGate from '../../components/PermissionsGate/PermissionsGate';
import Pagination from '../../components/Pagination/Pagination';
import Card from '../../components/Card/Card';
import Table from '../../components/Table/Table';
import TableHead from '../../components/Table/TableHead';
import TableRow from '../../components/Table/TableRow';
import TableHeadData from '../../components/Table/TableHeadData';
import TableBody from '../../components/Table/TableBody';
import Button from '../../components/Button/Button';
import ButtonTypes from '../../constants/buttonTypes';
import { IconCode, IconFileOk } from '../../assets/icon';
import TableData from '../../components/Table/TableData';
import Modal from '../../components/Modal/Modal';
import getPrinterTasks from '../../thunks/printerTasks/getPrinterTasks';
import Badge from '../../components/Badge/Badge';
import PrinterTasksStatus, { PrinterTasksStatusColor } from '../../constants/printerTasksStatus';
import PrinterActions from './components/PrinterActions';
import patchPrinterTasks from '../../thunks/printerTasks/patchPrinterTasks';

const logButtonClassName = 'h-[32px] w-[32px] p-0';
const logIconClassName = 'h-[18px] w-[18px] text-info';

function PrinterTasks() {
  const dispatch = useDispatch();
  const [logsData, setLogsData] = useState();
  const navigate = useNavigate();
  const {
    getPrinterTasks: {
      loading, data, currentPage, lastPage
    },
    patchPrinterTasks: {
      loading: patchLoading
    }
  } = useSelector((state) => ({
    getPrinterTasks: state?.getPrinterTasks,
    patchPrinterTasks: state?.patchPrinterTasks,
  }), shallowEqual);

  const breadcrumbs = {
    pageTitle: 'Printer Tasks',
    history: [
      {
        title: 'Home',
        onClick: () => navigate('/')
      },
      {
        title: 'Printer Tasks',
      },
    ]
  };

  useEffect(() => {
    if (!data) {
      dispatch(getPrinterTasks());
    }
  }, []);

  const onCancel = (id) => {
    dispatch(patchPrinterTasks({
      id,
      status: 6
    }));
  };

  const onPrint = (id) => {
    dispatch(patchPrinterTasks({
      id,
      status: 1
    }));
  };

  const onPagePress = (page) => dispatch(getPrinterTasks({ page }));

  return (
    <Layout breadcrumbs={breadcrumbs} pageTitle="Printer Task">
      <PermissionsGate
        can={permissions.PRINTER_READ}
        errorComponent={<AccessDenied />}
      >
        <Card
          showHeader={false}
          headerBordered={false}
          footer={(
            <div className="d-flex d-justify-end">
              <Pagination
                currentPage={currentPage}
                onPagePress={onPagePress}
                lastPage={lastPage}
              />
            </div>
          )}
          footerBordered={false}
        >
          <Table loading={loading || patchLoading}>
            <TableHead>
              <TableRow bordered>
                <TableHeadData />
                <TableHeadData>
                  id
                </TableHeadData>
                <TableHeadData>
                  time
                </TableHeadData>
                <TableHeadData>
                  name
                </TableHeadData>
                <TableHeadData>
                  file
                </TableHeadData>
                <TableHeadData>
                  status
                </TableHeadData>
                <TableHeadData>
                  printer
                </TableHeadData>
                <TableHeadData>
                  data
                </TableHeadData>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map(({
                id,
                name,
                status,
                options,
                doc_url: docUrl,
                created_at: date,
                printer: {
                  name: printerName
                }
                }, index) => (
                  <TableRow key={id} bordered={index < data.length - 1} dataRow={`${id}`}>
                    <TableData>
                      <PrinterActions
                        side={index < data.length - 3 ? 'right' : 'top'}
                        onPrint={() => onPrint(id)}
                        onCancel={() => onCancel(id)}
                      />
                    </TableData>
                    <TableData>{id}</TableData>
                    <TableData>
                      <Badge
                        text={DateTime.fromISO(date.replace(' ', 'T'), { zone: 'utc' })
                          .minus({ hours: 6 }).toFormat('yyyy-MM-dd HH:mm:ss')}
                        type={ButtonTypes.LIGHT}
                      />
                    </TableData>
                    <TableData>{name}</TableData>
                    <TableData>
                      {(docUrl?.includes('.png')
                          || docUrl?.includes('.jpg')
                          || docUrl?.includes('.jpeg')
                          || docUrl?.includes('.pdf'))
                        && (
                          <a href={docUrl} target="_blank" rel="noreferrer">
                            <IconFileOk color="#7239EA" />
                          </a>
                        )}
                    </TableData>
                    <TableData>
                      <Badge
                        text={PrinterTasksStatus[status]}
                        type={PrinterTasksStatusColor[status]}
                      />
                    </TableData>
                    <TableData>{printerName}</TableData>
                    <TableData>
                      <Button
                        containerStyle={logButtonClassName}
                        type={ButtonTypes.LIGHT}
                        icon={<IconCode className={logIconClassName} />}
                        onClick={() => setLogsData(options)}
                      />
                    </TableData>
                  </TableRow>
              ))}
            </TableBody>
          </Table>
          <Modal visible={!!logsData} toggleVisible={() => setLogsData(false)} hideSaveBtn>
            <div><pre>{JSON.stringify(logsData, null, 2)}</pre></div>
          </Modal>
        </Card>
      </PermissionsGate>
    </Layout>
  );
}

export default PrinterTasks;
